import React, { useState } from 'react';
import styled from 'styled-components';
import { useBetty } from '../../context/BettyContext';

interface Props {
  onClearHistory: () => void;
}

const HeaderContainer = styled.div`
  padding: 20px;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(255, 182, 193, 0.4);
  position: relative;
  z-index: 3;
  backdrop-filter: blur(10px);
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BettyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BettyAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff69b4, #ffb6c1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(255, 105, 180, 0.3);
  color: white;
  font-weight: bold;
`;

const BettyDetails = styled.div`
  h1 {
    margin: 0 0 4px 0;
    font-size: 1.5rem;
    color: #8b4513;
    font-family: 'Georgia', serif;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: rgba(139, 69, 19, 0.8);
    font-style: italic;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled.button<{ danger?: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${props => props.danger ? '#ff6b6b' : 'rgba(255, 182, 193, 0.4)'};
  background: ${props => props.danger ? 'transparent' : 'rgba(255, 255, 255, 0.7)'};
  color: ${props => props.danger ? '#ff6b6b' : '#8b4513'};
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.danger ? 'rgba(255, 107, 107, 0.1)' : 'rgba(255, 182, 193, 0.3)'};
    border-color: ${props => props.danger ? '#ff6b6b' : '#ff69b4'};
    color: ${props => props.danger ? '#ff6b6b' : '#8b4513'};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 182, 193, 0.2);
  }
`;

const StatusIndicator = styled.div<{ online: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: ${props => props.online ? '#4ade80' : '#f87171'};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    animation: ${props => props.online ? 'pulse 2s infinite' : 'none'};
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const ConfirmDialog = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: boolean }>`
  position: absolute;
  top: 100%;
  right: 20px;
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(184, 134, 11, 0.3);
  border-radius: 8px;
  padding: 16px;
  min-width: 250px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transform: translateY(${props => props.visible ? '8px' : '0'});
  transition: all 0.3s ease;
  z-index: 10;

  h3 {
    margin: 0 0 8px 0;
    color: #f4e4c1;
    font-size: 1rem;
  }

  p {
    margin: 0 0 12px 0;
    color: rgba(244, 228, 193, 0.8);
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
`;

export const BettyHeader: React.FC<Props> = ({ onClearHistory }) => {
  const { messages } = useBetty();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  // Check if Betty API is responsive (simplified)
  React.useEffect(() => {
    const BETTY_API_URL = process.env.NODE_ENV === 'production' 
      ? 'https://blagmanbros.com/api' 
      : 'http://localhost:8002/api';
      
    const checkConnection = async () => {
      try {
        const response = await fetch(`${BETTY_API_URL}/health`);
        setIsOnline(response.ok);
      } catch {
        setIsOnline(false);
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  const handleClearClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmClear = () => {
    onClearHistory();
    setShowConfirmDialog(false);
  };

  const handleCancelClear = () => {
    setShowConfirmDialog(false);
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (messages.length === 0) return 'Ready to help';
    return 'Active chat';
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <BettyInfo>
          <BettyAvatar>🎵</BettyAvatar>
          <BettyDetails>
            <h1>Betty</h1>
            <p>Your vintage music licensing assistant</p>
          </BettyDetails>
        </BettyInfo>

        <HeaderActions>
          <StatusIndicator online={isOnline}>
            {getStatusText()}
          </StatusIndicator>
          
          {messages.length > 0 && (
            <ActionButton danger onClick={handleClearClick}>
              Clear Chat
            </ActionButton>
          )}
        </HeaderActions>
      </HeaderContent>

      <ConfirmDialog visible={showConfirmDialog}>
        <h3>Clear Chat History?</h3>
        <p>
          This will delete all messages and start a fresh conversation with Betty. 
          This action cannot be undone.
        </p>
        <div className="actions">
          <ActionButton onClick={handleCancelClear}>
            Cancel
          </ActionButton>
          <ActionButton danger onClick={handleConfirmClear}>
            Clear All
          </ActionButton>
        </div>
      </ConfirmDialog>
    </HeaderContainer>
  );
};